import { Link } from "react-router-dom";
import { format } from "date-fns";
import { MessageSquare, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useScheduleFeedback } from "@/hooks/useScheduleFeedback";

interface ScheduleFeedbackDashboardCardProps {
  productionId: string;
}

const MAX_PREVIEW = 4;

/**
 * Producer Dashboard widget surfacing OPEN client feedback for a production.
 * Shares useScheduleFeedback with the nav badge and the schedule-page panel, so
 * counts stay in sync and it live-updates. Renders nothing when there's no open
 * feedback, so it acts as an attention card rather than empty noise.
 */
export default function ScheduleFeedbackDashboardCard({
  productionId,
}: ScheduleFeedbackDashboardCardProps) {
  const { openItems, openCount } = useScheduleFeedback(productionId);

  if (openCount === 0) return null;

  return (
    <Card className="border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold">
          <MessageSquare className="h-5 w-5" />
          Open client feedback
          <Badge variant="destructive">{openCount}</Badge>
        </h3>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/productions/${productionId}/schedule`}>
            View in schedule
            <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
      <ul className="space-y-2">
        {openItems.slice(0, MAX_PREVIEW).map((item) => (
          <li
            key={item.id}
            className="flex items-start justify-between gap-3 border-b border-border py-2 last:border-0"
          >
            <div className="min-w-0">
              <p className="truncate text-sm">
                <span className="font-medium">{item.author_name}</span>
                {item.crew_members?.name && (
                  <span className="text-muted-foreground"> · {item.crew_members.name}</span>
                )}
              </p>
              <p className="truncate text-xs text-muted-foreground">{item.body}</p>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">
              {format(new Date(item.created_at), "d MMM")}
            </span>
          </li>
        ))}
      </ul>
      {openCount > MAX_PREVIEW && (
        <p className="mt-3 text-xs text-muted-foreground">
          +{openCount - MAX_PREVIEW} more in the schedule
        </p>
      )}
    </Card>
  );
}
